<template>
  <div
      :class="['side-menu-shell', {'side-menu-shell--compact': collapsed}]"
      @mouseleave="handleMenuMouseLeave"
  >
    <div class="sidebar-rail">
      <div class="sidebar-brand sidebar-brand--rail">
        <!-- 使用客户确认的竖版品牌标识，保持侧栏收缩前后的品牌入口一致。 -->
        <img
            alt="清宸云翼"
            class="sidebar-brand__logo"
            src="@/assets/images/qcyy-brand-logo.svg"
        />
      </div>

      <nav class="primary-rail" aria-label="一级菜单">
        <button
            v-for="item in menuItems"
            :key="item.index"
            :aria-current="isCurrentPrimary(item) ? 'page' : null"
            :class="['primary-rail__item', {
              'is-current': isCurrentPrimary(item),
              'is-focused': focusedPrimary && focusedPrimary.index === item.index,
            }]"
            :title="item.title"
            type="button"
            @mouseenter="previewPrimary(item)"
            @click="handlePrimaryClick(item)"
        >
          <i :class="item.icon"></i>
          <span>{{ getPrimaryShortTitle(item) }}</span>
        </button>
      </nav>

      <!-- 收缩按钮由布局层统一管理宽度，避免侧栏自身状态与页面布局不同步。 -->
      <button
          :aria-label="collapsed ? '展开侧边栏' : '收缩侧边栏'"
          :title="collapsed ? '展开侧边栏' : '收缩侧边栏'"
          class="sidebar-rail__footer"
          type="button"
          @click="$emit('toggle-collapse')"
      >
        <i :class="collapsed ? 'el-icon-d-arrow-right' : 'el-icon-d-arrow-left'"></i>
      </button>
    </div>

    <section v-if="!collapsed" class="sidebar-context" aria-label="当前模块菜单">
      <!-- 一级菜单切换时重建内容区域，触发轻量进场动画。 -->
      <div
          :key="focusedPrimary ? focusedPrimary.index : ''"
          class="sidebar-context__content"
      >
        <div class="sidebar-context__header">
          <div>
            <h2>{{ focusedPrimaryTitle }}</h2>
            <p>{{ focusedPrimarySubtitle }}</p>
          </div>
        </div>

        <el-input
            v-model.trim="menuKeyword"
            class="sidebar-context__search"
            clearable
            placeholder="搜索菜单 / 功能"
            prefix-icon="el-icon-search"
            size="small"
        />

        <div class="context-menu">
          <SideContextNode
              v-for="item in visibleContextMenus"
              :key="item.index"
              :active-index="currentActiveIndex"
              :level="1"
              :node="item"
              @navigate="navigateTo"
          />
          <el-empty
              v-if="!visibleContextMenus.length"
              :image-size="58"
              description="未找到匹配菜单"
          />
        </div>
      </div>

      <div class="sidebar-footer">
        <!-- 侧边栏底部只保留当前端口归属，不再展示原型标签。 -->
        <p>{{ platform.userName }}</p>
      </div>
    </section>
  </div>
</template>

<script>
const PRIMARY_SHORT_TITLES = {
  工作台: '首页',
  销售管理: '销售',
  项目履约: '项目',
  财务管理: '财务',
  运营中心: '运营',
  业务配置: '配置',
  系统管理: '系统',
}

function findMenuTrail(menuItems, targetIndex, parents = []) {
  for (const item of menuItems) {
    if (item.index === targetIndex) {
      return parents.concat(item)
    }
    if (item.children && item.children.length) {
      const childTrail = findMenuTrail(item.children, targetIndex, parents.concat(item))
      if (childTrail.length) {
        return childTrail
      }
    }
  }
  return []
}

function findMenuItem(menuItems, targetIndex) {
  return findMenuTrail(menuItems, targetIndex).pop() || null
}

function findFirstLeaf(item) {
  if (!item) {
    return null
  }
  if (!item.children || !item.children.length) {
    return item
  }
  for (const child of item.children) {
    const leaf = findFirstLeaf(child)
    if (leaf) {
      return leaf
    }
  }
  return null
}

function filterMenuTree(menuItems, keyword) {
  const query = keyword.trim().toLowerCase()
  if (!query) {
    return menuItems
  }
  return menuItems
      .map((item) => {
        const children = item.children ? filterMenuTree(item.children, keyword) : []
        const matched = item.title.toLowerCase().includes(query)
        if (matched || children.length) {
          return {...item, children: matched ? (item.children || []) : children}
        }
        return null
      })
      .filter(Boolean)
}

const SideContextNode = {
  name: 'SideContextNode',
  props: {
    node: {
      type: Object,
      required: true,
    },
    activeIndex: {
      type: String,
      default: '',
    },
    level: {
      type: Number,
      default: 1,
    },
  },
  methods: {
    emitNavigate(node) {
      if (!node.children || !node.children.length) {
        this.$emit('navigate', node.index)
      }
    },
  },
  render(h) {
    const children = this.node.children || []
    if (children.length) {
      return h('div', {
        class: ['context-menu__group', `context-menu__group--level-${this.level}`],
      }, [
        h('div', {class: 'context-menu__group-title'}, [
          h('span', {class: 'context-menu__group-title-main'}, [
            h('i', {class: this.node.icon}),
            h('span', this.node.title),
          ]),
          h('i', {class: 'el-icon-arrow-up context-menu__group-arrow'}),
        ]),
        h('div', {class: 'context-menu__children'}, children.map((child) => h(SideContextNode, {
          key: child.index,
          props: {
            node: child,
            activeIndex: this.activeIndex,
            level: this.level + 1,
          },
          on: {
            navigate: (index) => this.$emit('navigate', index),
          },
        }))),
      ])
    }
    return h('button', {
      class: ['context-menu__leaf', `context-menu__leaf--level-${this.level}`, {
        'is-active': this.node.index === this.activeIndex,
      }],
      attrs: {
        type: 'button',
        title: this.node.title,
        'aria-current': this.node.index === this.activeIndex ? 'page' : null,
      },
      on: {
        click: () => this.emitNavigate(this.node),
      },
    }, [
      h('span', {class: 'context-menu__leaf-dot'}),
      h('i', {class: this.node.icon}),
      h('span', {class: 'context-menu__leaf-title'}, this.node.title),
    ])
  },
}

export default {
  name: 'SideMenu',
  components: {
    SideContextNode,
  },
  props: {
    platform: {
      type: Object,
      required: true,
    },
    menuItems: {
      type: Array,
      default: () => [],
    },
    collapsed: {
      type: Boolean,
      default: false,
    },
    activeIndex: {
      type: String,
      default: '',
    },
    allowCollapsedPreview: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      focusedPrimaryIndex: '',
      // 悬浮预览只在侧栏内生效，离开整个侧栏后恢复当前路由菜单。
      hoveredPrimaryIndex: '',
      menuKeyword: '',
    }
  },
  computed: {
    currentActiveIndex() {
      // 详情页等动态路由可由父级指定菜单索引，保持侧边栏选中态稳定。
      return this.activeIndex || this.$route.path
    },
    activeTrail() {
      return findMenuTrail(this.menuItems, this.currentActiveIndex)
    },
    activePrimary() {
      return this.activeTrail[0] || null
    },
    hoveredPrimary() {
      return findMenuItem(this.menuItems, this.hoveredPrimaryIndex)
    },
    focusedPrimary() {
      // 悬浮仅临时覆盖点击或路由确定的菜单，不改变当前页面路由。
      return this.hoveredPrimary || findMenuItem(this.menuItems, this.focusedPrimaryIndex) || this.activePrimary || this.menuItems[0] || null
    },
    focusedPrimaryTitle() {
      return this.focusedPrimary ? this.focusedPrimary.title : this.platform.label
    },
    focusedPrimarySubtitle() {
      const children = (this.focusedPrimary && this.focusedPrimary.children) || []
      if (!children.length) {
        return this.platform.subtitle || '项目运营中心'
      }
      return children.map((item) => item.title).join(' / ')
    },
    visibleContextMenus() {
      if (!this.focusedPrimary || !this.focusedPrimary.children) {
        return []
      }
      return filterMenuTree(this.focusedPrimary.children, this.menuKeyword)
    },
  },
  watch: {
    currentActiveIndex: {
      immediate: true,
      handler() {
        this.hoveredPrimaryIndex = ''
        if (this.activePrimary) {
          this.focusedPrimaryIndex = this.activePrimary.index
        }
      },
    },
    collapsed(value) {
      if (value) {
        this.restoreActivePrimary()
      }
    },
  },
  methods: {
    getPrimaryShortTitle(item) {
      return PRIMARY_SHORT_TITLES[item.title] || item.title.slice(0, 2)
    },
    isCurrentPrimary(item) {
      return Boolean(this.activePrimary && this.activePrimary.index === item.index)
    },
    previewPrimary(item) {
      if (!item.children || !item.children.length) {
        return
      }
      if (this.collapsed && !this.allowCollapsedPreview) {
        return
      }
      this.hoveredPrimaryIndex = item.index
      // 首页紧凑侧栏由布局层临时展开，避免悬浮预览改变当前页面路由。
      if (this.collapsed && this.allowCollapsedPreview) {
        this.$emit('preview-expand')
      }
    },
    clearHoveredPrimary() {
      this.hoveredPrimaryIndex = ''
    },
    restoreActivePrimary() {
      this.clearHoveredPrimary()
      if (this.activePrimary) {
        this.focusedPrimaryIndex = this.activePrimary.index
      }
    },
    handleMenuMouseLeave() {
      this.restoreActivePrimary()
      this.$emit('preview-collapse')
    },
    handlePrimaryClick(item) {
      this.clearHoveredPrimary()
      this.focusedPrimaryIndex = item.index
      if (!item.children || !item.children.length) {
        this.navigateTo(item.index)
        return
      }
      // 窄屏只显示一级 rail，点击容器时跳到第一个可见叶子，避免无法展开二三级。
      if (this.collapsed) {
        const firstLeaf = findFirstLeaf(item)
        if (firstLeaf) {
          this.navigateTo(firstLeaf.index)
        }
      }
    },
    navigateTo(index) {
      if (!index || index === this.$route.path) {
        return
      }
      this.$router.push(index).catch(() => {
      })
    },
  },
}
</script>
